import { useState } from "react";
import { Box, Collapse, Link, Stack, Typography } from "@mui/material";
import {
  Description,
  CalendarMonth,
  Update,
  NoteAlt,
  Person,
  LocationOn,
  Phone,
  ErrorOutline,
} from "@mui/icons-material";
import { IGeneralHistory, IGroupedHistory } from "../../../redux/common/common.types";
import { sanitizeHistoryMessage } from "../../../helpers/historyHelpers";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";
import { LeadStatusRenderer } from "../../../components/LeadStatusRenderer/LeadStatusRenderer";
import { EventChip, getEventColor } from "../../../components/ChipsRenderer/EventChip";
import { formatDate } from "../../../helpers";

const getIconForField = (key: string) => {
  switch (key.toLowerCase()) {
    case "status":
    case "derived_status":
    case "drive_status_uuid":
      return Update;

    case "function_date":
    case "booking_date":
    case "date_of_visit":
    case "followup_date":
    case "event_time":
      return CalendarMonth;

    case "handled_by":
    case "modified_by_name":
    case "created_by_name":
    case "name":
    case "name_of_guest":
      return Person;

    case "preferred_venue":
    case "company_outlet_name":
      return LocationOn;

    case "remarks":
    case "cancellation_dead_reason":
      return NoteAlt;

    case "contact_number_one":
    case "phone":
      return Phone;

    default:
      return Description;
  }
};

const isImportantField = (key: string): boolean => {
  const importantFields = [
    "name",
    "name_of_guest",
    "contact_number_one",
    "phone",
    "function_date",
    "booking_date",
    "date_of_visit",
    "status",
    "derived_status",
  ];
  return importantFields.includes(key.toLowerCase());
};

const EXCLUDED_HISTORY_FIELDS = new Set([
  "company_outlet_name",
  "modified_by_name",
  "modified_by_uuid",
  "customer_fact_uuid",
]);

const ALLOWED_HISTORY_FIELDS = [
  "status",
  "name_of_guest",
  "contact_number_one",
  "contact_number_two",
  "additional_contact_number",
  "email",
  "event_type",
  "other_event_name",
  "function_date",
  "function_month",
  "event_time",
  "date_of_visit",
  "booking_date",
  "preferred_venue",
  "followup_date",
  "followup_time_slot",
  "number_of_guests",
  "number_of_rooms",
  "estimated_rough_revenue",
  "guest_budget",
  "quantity",
  "priority",
  "source",
  "payment_mode",
  "handled_by_employee",
  "description",
];

const FIELD_LABELS: Record<string, string> = {
  name_of_guest: "Guest Name",
  contact_number_one: "Contact Number",
  contact_number_two: "Alternate Contact Number",
  additional_contact_number: "Additional Contacts",
  function_date: "Event Date",
  function_month: "Event Month",
  followup_date: "Followup Date",
};

/** Renders arrays of contacts ({ name, country_code, contact_number, ... }) readably. */
const formatHistoryObjectValue = (value: any): string => {
  if (Array.isArray(value)) {
    if (value.length === 0) return "--";
    return value
      .map((item) => {
        if (item && typeof item === "object" && "contact_number" in item) {
          const relation = item.relation_with_customer ? ` (${item.relation_with_customer})` : "";
          const phone = `${item.country_code || ""} ${item.contact_number || ""}`.trim();
          return `${item.name || ""}${relation}${item.name ? " - " : ""}${phone}`;
        }
        return typeof item === "object" ? JSON.stringify(item) : String(item);
      })
      .join(", ");
  }
  return JSON.stringify(value);
};

const getIconColor = (key: string) => {
  if (key.toLowerCase().includes("status")) return "#2e7d32";
  if (key.toLowerCase().includes("date")) return "#1976d2";
  if (key.toLowerCase() === "remarks") return "#f9a825";
  return "#666";
};

const getAvatarColor = (name: string): string => {
  const colors = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2", "#d32f2f", "#0097a7", "#5d4037"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const formatHistoryMessage = (
  data: IGeneralHistory,
): Array<{ key: string; message: React.ReactNode; icon: any }> => {
  {
    const changedColumns = data.payload?.changed_column || {};

    const messages: Array<{
      key: string;
      message: React.ReactNode;
      icon: any;
    }> = [];

    let hasFieldChanges = false;

    Object.entries(changedColumns).forEach(([key, value]: any) => {
      const normalizedKey = key.toLowerCase();

      if (EXCLUDED_HISTORY_FIELDS.has(normalizedKey)) return;
      if (!ALLOWED_HISTORY_FIELDS.includes(normalizedKey)) return;
      if (normalizedKey.includes("uuid")) return;
      if (normalizedKey === "remarks") return;

      const readableKey =
        FIELD_LABELS[normalizedKey] ||
        key.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase());

      let oldValue = value.old_value ?? "--";
      let newValue = value.new_value ?? "--";

      if (typeof oldValue === "object" && oldValue !== null) {
        oldValue = formatHistoryObjectValue(oldValue);
      }
      if (typeof newValue === "object" && newValue !== null) {
        newValue = formatHistoryObjectValue(newValue);
      }
      oldValue = String(oldValue);
      newValue = String(newValue);

      const isoTimestampRegex = /^\d{4}-\d{2}-\d{2}T[\d:.]+Z$/;
      if (isoTimestampRegex.test(oldValue)) {
        oldValue = oldValue.split("T")[0];
      }
      if (isoTimestampRegex.test(newValue)) {
        newValue = newValue.split("T")[0];
      }
      if (oldValue === newValue) return;

      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (dateRegex.test(oldValue)) {
        oldValue = formatDate(oldValue);
      }
      if (dateRegex.test(newValue)) {
        newValue = formatDate(newValue);
      }

      const isOldEmpty =
        !oldValue ||
        oldValue === "--" ||
        oldValue === "null" ||
        oldValue === "undefined" ||
        oldValue.trim() === "";

      const isNewEmpty =
        !newValue ||
        newValue === "--" ||
        newValue === "null" ||
        newValue === "undefined" ||
        newValue.trim() === "";

      const isAdded = isOldEmpty && !isNewEmpty;

      const isImportant = isImportantField(key);
      const IconComponent = getIconForField(key);

      messages.push({
        key,
        icon: IconComponent,
        message: (
          <Typography
            component="span"
            sx={{
              fontSize: "13px",
              display: "block",
              color: isImportant ? "#1976d2" : "#333",
            }}
          >
            {isAdded ? "added" : "changed"}{" "}
            <strong style={{ color: isImportant ? "#d32f2f" : "#555", fontWeight: 600 }}>
              {readableKey}
            </strong>{" "}
            {!isAdded && (
              <>
                from <span style={{ color: "#888" }}>{oldValue}</span>{" "}
              </>
            )}
            to{" "}
            <span style={{ color: isImportant ? "#4caf50" : "#666", fontWeight: isImportant ? 500 : 400 }}>
              {newValue}
            </span>
          </Typography>
        ),
      });
      hasFieldChanges = true;
    });

    if (!hasFieldChanges && data.message) {
      const sanitizedMessage = sanitizeHistoryMessage(data.message);

      messages.push({
        key: "message",
        icon: Description,
        message: (
          <Typography sx={{ fontSize: "13px", display: "block" }}>
            {sanitizedMessage}
          </Typography>
        ),
      });
    }

    // NOTE: the remark is intentionally NOT pushed here. When records are
    // grouped, the remark is shared across the group and rendered once as a
    // group-level footer (see the group component below).
    return messages;
  }
};

/**
 * Renders a single history item's status/event chips and its field-change
 * lines. The per-item remark is NOT rendered here — the shared remark is shown
 * once at the group level by HistoryRow.
 */
const HistoryItemBody: React.FC<{ data: IGeneralHistory }> = ({ data }) => {
  const messages = formatHistoryMessage(data);
  const eventType = data?.lead?.event_type || "";

  // Show cancellation/dead reason when this record moved the lead to DEAD/CANCELLED.
  const actualStatus = (data?.lead?.actual_status || "").toUpperCase();
  const isDeadOrCancelled = ["DEAD", "CANCELLED"].includes(actualStatus)
  const cancellationReason =
    data?.payload?.changed_column?.cancellation_dead_reason?.new_value ||
    data?.lead?.cancellation_dead_reason ||
    "";
  const showCancellationReason = isDeadOrCancelled && !!cancellationReason;

  return (
    <Box sx={{ mb: 1.25 }}>
      {/* Status + Event type row (per record) */}
      {(data?.lead?.actual_status || eventType) && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
          {eventType && (
            <EventChip eventType={eventType} otherEventName={data?.lead?.other_event_name || ""} />
          )}
          {data?.lead?.actual_status && (
            <LeadStatusRenderer
              status={data?.lead?.status || ""}
              actualStatus={data?.lead?.actual_status || ""}
            />
          )}
        </Stack>
      )}

      {/* Per-record bordered box holding this record's changes */}
      {(messages.length > 0 || showCancellationReason) && (
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            bgcolor: "#fafbfc",
            p: 1.25,
          }}
        >
          <Stack spacing={0.75}>
            {messages.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                >
                  <IconComponent
                    sx={{ fontSize: 16, color: getIconColor(item.key), mt: "2px" }}
                  />
                  <Box sx={{ flex: 1 }}>{item.message}</Box>
                </Box>
              );
            })}

            {/* Cancellation reason — rendered last, divided by a light border */}
            {showCancellationReason && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mt: 0.75,
                  pt: 0.75,
                  borderTop: "1px solid #eaeaea",
                }}
              >
                <ErrorOutline sx={{ fontSize: 16, color: "#d32f2f", mt: "2px" }} />
                <Typography
                  component="span"
                  sx={{ fontSize: "13px", display: "block", color: "#333" }}
                >
                  <strong style={{ fontWeight: 600, color: "#d32f2f" }}>
                    Cancellation Reason
                  </strong>
                  {" : "}
                  <span style={{ color: "#555" }}>{cancellationReason}</span>
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const REMARK_PREVIEW_LENGTH = 100;

/** Remark line with a 50-char preview and an animated Read more / Read less toggle. */
const RemarkText: React.FC<{ remark: string }> = ({ remark }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = remark.length > REMARK_PREVIEW_LENGTH;
  const preview = isLong
    ? `${remark.slice(0, REMARK_PREVIEW_LENGTH).trimEnd()}…`
    : remark;

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 0.5, pl: 1.25 }}>
      <NoteAlt sx={{ fontSize: 16, color: getIconColor("remarks"), mt: "2px" }} />
      <Typography
        component="div"
        sx={{ fontSize: "13px", color: "#333", flex: 1 }}
      >
        <strong style={{ fontWeight: 600, color: "#555" }}>Remark</strong>
        {" : "}
        {/* Collapsed preview — hidden while expanded */}
        {isLong && !expanded && (
          <span style={{ color: "#2e7d32", fontWeight: 600 }}>{preview}</span>
        )}

        {/* Full text — animated open/close */}
        {isLong ? (
          <Collapse in={expanded} timeout={250} unmountOnExit>
            <span style={{ color: "#2e7d32", fontWeight: 600 }}>{remark}</span>
          </Collapse>
        ) : (
          <span style={{ color: "#2e7d32", fontWeight: 600 }}>{remark}</span>
        )}

        {isLong && (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => setExpanded((prev) => !prev)}
            sx={{
              display: "inline-block",
              mt: expanded ? 0.25 : 0,
              ml: expanded ? 0 : 0.5,
              fontSize: "12px",
              fontWeight: 600,
              color: "#1976d2",
              verticalAlign: "baseline",
            }}
          >
            {expanded ? "Read less" : "Read more"}
          </Link>
        )}
      </Typography>
    </Box>
  );
};

export const HistoryRow: React.FC<{
  group: IGroupedHistory;
  isLast?: boolean;
  selection?: () => void;
}> = ({ group, isLast = false }) => {
  const { ftDateTime } = useTimezoneFormatter();

  const firstItem = group.items[0];
  const userName =
    firstItem?.created_by_name || firstItem?.modified_by_name || "User";
  const timestamp = ftDateTime(group.latestTs);
  const commonRemark = group.commonRemark;
  // Dot color follows the first record's event type.
  const dotColor = getEventColor(firstItem?.lead?.event_type) || getAvatarColor(userName);

  return (
    <Box sx={{ display: "flex", gap: 1.5 }}>
      {/* Left: timeline dot (colored by first event type) + connecting line */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: 16,
        }}
      >
        {/* Dot vertically centered against the header line (~24px tall) */}
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            bgcolor: dotColor,
            mt: "5px",
            flexShrink: 0,
            zIndex: 2,
          }}
        />
        {!isLast && (
          <Box
            sx={{
              width: 0,
              flex: 1,
              borderLeft: "2px dashed #ccc",
              mt: 0.75,
            }}
          />
        )}
      </Box>

      {/* Right: Header + Changes card */}
      <Box sx={{ flex: 1, pb: 2.5 }}>
        {/* Header (rendered once per group) */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#1a1a1a" }}>
              {userName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              has made changes
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {timestamp}
          </Typography>
        </Stack>

        {/* Each merged record: own chip row + own bordered change box */}
        {group.items.map((item, index) => (
          <HistoryItemBody
            key={item.history_uuid || item.history_id || index}
            data={item}
          />
        ))}

        {/* Common remark — inline, no box, 50-char preview + Read more toggle */}
        {commonRemark && <RemarkText remark={commonRemark} />}
      </Box>
    </Box>
  );
};
